/**
 * Inbox mint STD picker.
 *
 * Unarmed: existing wall-clock + random pad (suggestDeparture), plus the
 * airborne +4h clamp. Armed Duty Standby: majority of new RFQs land inside
 * the watch window, soon enough to accept and go (~30–60 min readiness —
 * not a hard airborne-in-30-min rule). A smaller share stays 1–2 days
 * ahead for planning variety.
 *
 * Classic seasonal / demo / trickle / on-demand mint use this directly.
 * Grok Fetch is told the same doctrine in the Inbox brief, then clamped
 * here so a "next 14 days" completion cannot ignore an armed watch.
 */
import { isAirbornePhase, type AircraftState } from "./aircraftState";
import { roundUpToQuarter, suggestDeparture } from "./clock";
import type { DutyStandbyState, DutyStandbyWindow } from "./types";

export type InboxStandbySnap = Pick<DutyStandbyState, "armed" | "windows">;

export type InboxStdPickInput = {
  now: Date;
  aircraftState?: AircraftState | null;
  dutyStandby?: InboxStandbySnap | null;
  rng?: () => number;
};

export type StandbyStdWindow = {
  startMs: number;
  endMs: number;
  /** Explicit calendar block vs implicit "whole watch" while armed. */
  implicit: boolean;
  label?: string;
};

/** Majority share of armed-standby STDs stay inside the watch / go-soon band. */
export const STANDBY_STD_IN_WINDOW_RATE = 0.7;
/** Ops readiness floor — accept and go, not "must be airborne in 30 min". */
export const STANDBY_STD_READINESS_MIN_MS = 30 * 60_000;
export const STANDBY_STD_READINESS_MAX_MS = 60 * 60_000;
/** Slack past readiness so a 90-min / 2-hour STD still counts as soon. */
export const STANDBY_STD_GO_SOON_MS = 3 * 60 * 60_000;
/** Empty windows = the whole watch from now. */
export const STANDBY_STD_IMPLICIT_WATCH_MS = 6 * 60 * 60_000;
export const STANDBY_STD_PLANNING_DAY_MIN = 1;
export const STANDBY_STD_PLANNING_DAY_MAX = 2;

const DAY_MS = 24 * 60 * 60_000;

function parseWindow(w: DutyStandbyWindow): { startMs: number; endMs: number; label?: string } | null {
  const startMs = Date.parse(w.startUtc);
  const endMs = Date.parse(w.endUtc);
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) return null;
  return { startMs, endMs, label: w.label || undefined };
}

/** Current / next armed watch used for mint STD. Null when cold or all windows expired. */
export function resolveArmedStandbyStdWindow(
  standby: InboxStandbySnap | null | undefined,
  now: Date,
): StandbyStdWindow | null {
  if (!standby?.armed) return null;
  const t = now.getTime();
  const windows = (standby.windows ?? [])
    .map(parseWindow)
    .filter((w): w is NonNullable<typeof w> => Boolean(w))
    .sort((a, b) => a.startMs - b.startMs);

  if (!windows.length) {
    return { startMs: t, endMs: t + STANDBY_STD_IMPLICIT_WATCH_MS, implicit: true, label: "whole watch" };
  }

  const current = windows.find((w) => t >= w.startMs && t <= w.endMs);
  if (current) return { ...current, implicit: false };

  const next = windows.find((w) => w.startMs > t);
  if (next) return { ...next, implicit: false };

  return null;
}

export function inboxStdInStandbyWindow(std: Date, window: StandbyStdWindow): boolean {
  const t = std.getTime();
  return t >= window.startMs && t <= window.endMs;
}

/** Soon enough to accept and go while on watch — not a hard 30-min airborne rule. */
export function inboxStdIsSoonStandby(std: Date, now: Date, window: StandbyStdWindow): boolean {
  const t = std.getTime();
  if (!inboxStdInStandbyWindow(std, window)) return false;
  const nowMs = now.getTime();
  const readinessFloor = nowMs + STANDBY_STD_READINESS_MIN_MS - 5 * 60_000;
  if (t < Math.max(window.startMs, readinessFloor - 15 * 60_000)) return false;
  if (nowMs >= window.startMs && nowMs <= window.endMs) {
    return t <= nowMs + STANDBY_STD_GO_SOON_MS;
  }
  return t <= window.startMs + STANDBY_STD_GO_SOON_MS;
}

export function inboxStdIsPlanningVariety(std: Date, now: Date): boolean {
  const delta = std.getTime() - now.getTime();
  return delta >= STANDBY_STD_PLANNING_DAY_MIN * DAY_MS - 2 * 60 * 60_000 &&
    delta <= (STANDBY_STD_PLANNING_DAY_MAX + 1) * DAY_MS;
}

function readinessOffsetMs(rng: () => number): number {
  return STANDBY_STD_READINESS_MIN_MS + rng() * (STANDBY_STD_READINESS_MAX_MS - STANDBY_STD_READINESS_MIN_MS);
}

function airborneFloorMs(now: Date, state?: AircraftState | null): number {
  if (state && isAirbornePhase(state.phase)) return now.getTime() + 4 * 60 * 60_000;
  return now.getTime();
}

function pickStdInsideStandbyWindow(
  now: Date,
  window: StandbyStdWindow,
  state: AircraftState | null | undefined,
  rng: () => number,
): Date {
  const nowMs = now.getTime();
  const floor = Math.max(airborneFloorMs(now, state), nowMs + readinessOffsetMs(rng), window.startMs);
  const soonEnd = Math.min(window.endMs, Math.max(floor, window.startMs) + STANDBY_STD_GO_SOON_MS);
  const end = Math.max(floor, soonEnd);
  if (end <= floor) return roundUpToQuarter(new Date(floor));
  const span = end - floor;
  const t = floor + rng() * span;
  return roundUpToQuarter(new Date(t));
}

function pickPlanningVarietyStd(now: Date, rng: () => number): Date {
  const extraDays = STANDBY_STD_PLANNING_DAY_MIN + Math.floor(rng() * (STANDBY_STD_PLANNING_DAY_MAX - STANDBY_STD_PLANNING_DAY_MIN + 1));
  const base = new Date(now.getTime() + extraDays * DAY_MS);
  return suggestDeparture(base, rng);
}

function defaultDeskStd(now: Date, state: AircraftState | null | undefined, rng: () => number): Date {
  const base = suggestDeparture(now, rng);
  const floor = airborneFloorMs(now, state);
  return floor > now.getTime() ? new Date(Math.max(base.getTime(), floor)) : base;
}

/**
 * Classic mint / Grok fallback STD.
 * Armed + usable window → majority soon-in-window, minority 1–2 days ahead.
 * Unarmed or expired windows → current suggestDeparture spread.
 */
export function pickInboxDeparture(input: InboxStdPickInput): Date {
  const rng = input.rng ?? Math.random;
  const now = input.now;
  const window = resolveArmedStandbyStdWindow(input.dutyStandby, now);
  if (!window) return defaultDeskStd(now, input.aircraftState, rng);
  if (rng() < STANDBY_STD_IN_WINDOW_RATE) {
    return pickStdInsideStandbyWindow(now, window, input.aircraftState, rng);
  }
  return pickPlanningVarietyStd(now, rng);
}

/**
 * Keep a usable Grok STD when it already matches the rolled bucket;
 * otherwise clamp. Unarmed leaves the candidate alone.
 */
export function applyInboxStdBias(candidate: Date, input: InboxStdPickInput): Date {
  const rng = input.rng ?? Math.random;
  const now = input.now;
  const window = resolveArmedStandbyStdWindow(input.dutyStandby, now);
  if (!window) return candidate;
  if (rng() < STANDBY_STD_IN_WINDOW_RATE) {
    if (inboxStdIsSoonStandby(candidate, now, window)) return candidate;
    return pickStdInsideStandbyWindow(now, window, input.aircraftState, rng);
  }
  if (inboxStdIsPlanningVariety(candidate, now)) return candidate;
  return pickPlanningVarietyStd(now, rng);
}

export function formatStandbyStdPromptBlock(
  standby: InboxStandbySnap | null | undefined,
  now: Date,
): string {
  const window = resolveArmedStandbyStdWindow(standby, now);
  if (!window) return "";
  const start = new Date(window.startMs).toISOString();
  const end = new Date(window.endMs).toISOString();
  const where = window.implicit
    ? `Duty Standby is armed with no calendar windows — the whole watch (${start} → ${end}).`
    : `Duty Standby is armed. Active / next window${window.label ? ` (${window.label})` : ""}: ${start} → ${end}.`;
  return [
    where,
    "STD bias (standby-window): a majority of departureIso / STD values MUST fall inside that armed standby window, soon enough to accept and go (readiness ~30–60 minutes — not a hard airborne-in-30-min rule).",
    "Keep a smaller share of 1–2 day ahead RFQs for planning variety.",
    "Do not put most STDs 1–2 days out while the watch is live.",
  ].join(" ");
}
